import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../services/api";
import toast from "react-hot-toast";
import AdminLayout from "../layouts/AdminLayout";
import PageTitle from "../components/ui/PageTitle";
import DataTable from "../components/DataTable";
import { Plus } from "lucide-react";

export default function Candidates() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  let currentFilter = "All";
  let pageSubtitle = "Manage all candidates applied for various jobs.";
  
  if (location.pathname.includes("active")) {
    currentFilter = "Active";
    pageSubtitle = "Candidates currently in the hiring pipeline.";
  } else if (location.pathname.includes("shortlisted")) {
    currentFilter = "Shortlisted";
    pageSubtitle = "Candidates shortlisted for further rounds.";
  } else if (location.pathname.includes("rejected")) {
    currentFilter = "Rejected";
    pageSubtitle = "Candidates who were not selected.";
  }

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const response = await api.get("/candidates", {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      let allCandidates = response.data.data || [];
      
      if (currentFilter === "Active") {
        allCandidates = allCandidates.filter(c => c.status !== "Rejected");
      } else if (currentFilter === "Shortlisted") {
        allCandidates = allCandidates.filter(c => c.status === "Shortlisted" || c.status === "shortlisted");
      } else if (currentFilter === "Rejected") {
        allCandidates = allCandidates.filter(c => c.status === "Rejected" || c.status === "rejected");
      }

      setCandidates(allCandidates);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      toast.error("Failed to load candidates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, [location.pathname]);

  const handleDelete = async (candidate) => {
    if (window.confirm(`Are you sure you want to delete ${candidate.name}?`)) {
      try {
        const token = localStorage.getItem("admin_token");
        await api.delete(`/candidates/${candidate.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Candidate deleted successfully");
        setCandidates(candidates.filter(c => c.id !== candidate.id));
      } catch (error) {
        toast.error("Failed to delete candidate");
      }
    }
  };

  const handleView = (candidate) => {
    navigate(`/candidates/view/${candidate.id}`);
  };

  const columns = [
    { key: "name", label: "Full Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "job", label: "Applied For", format: (job) => job ? job.title : "N/A" },
    // 🌟 NAYA COLUMN ADD KIYA HAI WALLET BALANCE KE LIYE
    { key: "coins", label: "Wallet Balance", format: (coins) => coins ? `${coins} 🪙` : "0 🪙" },
    { key: "status", label: "Status" },
  ];

  return (
    <AdminLayout>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <PageTitle title={`${currentFilter} Candidates`} subtitle={pageSubtitle} />
        
        <button
          onClick={() => navigate("/candidates/new")}
          className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2 px-6 rounded-lg transition-colors"
        >
          <Plus size={18} /> Add Candidate
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      ) : (
        <DataTable
          title="Candidates List"
          columns={columns}
          data={candidates}
          onView={handleView}
          onEdit={(row) => navigate(`/candidates/edit/${row.id}`)}
          onDelete={handleDelete}
        />
      )}
    </AdminLayout>
  );
}